import React, {useEffect, useRef} from 'react';
import './CampfireSparks.css';

const CAMPFIRE_COLORS = [
  {r: 255, g: 140, b: 0},
  {r: 255, g: 200, b: 50},
  {r: 255, g: 80, b: 0},
  {r: 255, g: 100, b: 20},
  {r: 212, g: 175, b: 55},
];

function randomRange(min, max) {
  return Math.random() * (max - min) + min;
}

function randomColor() {
  return CAMPFIRE_COLORS[Math.floor(Math.random() * CAMPFIRE_COLORS.length)];
}

/**
 * Искры костра — фоновый слой на весь экран.
 *
 * Холст прозрачный (каждый кадр clearRect) и рисует только свет, поэтому фоном
 * эффекту служит то, что лежит под ним: темный neutral в меню, красный оверлей
 * во вступительной колоде. Своего фона у него нет и заводить его не нужно.
 *
 * sparkScale увеличивает искры, не трогая ни их количество, ни свечение
 * костра. Живет в ref, а не в зависимостях эффекта: смена значения не должна
 * перезапускать анимацию с пустого поля.
 */
const CampfireSparks = ({sparkScale = 1}) => {
  const canvasRef = useRef(null);
  const sparkScaleRef = useRef(sparkScale);
  sparkScaleRef.current = sparkScale;
  const particlesRef = useRef([]);
  const animRef = useRef(null);
  const spawnTimerRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // В jsdom (тесты монтирующих компонентов) контекста нет — рисовать нечего
    if (!ctx) return undefined;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const maxParticles = 180;

    // Many soft glow blobs arranged along the left edge, lower half.
    // Each blob drifts chaotically toward random targets, creating
    // an organic, ever-shifting campfire shape.
    const blobDefs = [
      {
        baseX: -120,
        baseYRel: 0.88,
        rBase: 0.52,
        rVar: 0.16,
        color: '255, 50, 5',
        baseA: 0.065,
        pulse: 0.50,
        phase: 0.0,
        driftSpeed: 0.35,
        driftRange: 45
      },
      {
        baseX: -60,
        baseYRel: 0.95,
        rBase: 0.44,
        rVar: 0.13,
        color: '255, 90, 15',
        baseA: 0.055,
        pulse: 0.75,
        phase: 1.3,
        driftSpeed: 0.45,
        driftRange: 38
      },
      {
        baseX: -170,
        baseYRel: 0.80,
        rBase: 0.40,
        rVar: 0.11,
        color: '255, 130, 30',
        baseA: 0.048,
        pulse: 0.60,
        phase: 2.7,
        driftSpeed: 0.25,
        driftRange: 52
      },
      {
        baseX: -30,
        baseYRel: 1.02,
        rBase: 0.34,
        rVar: 0.09,
        color: '255, 170, 50',
        baseA: 0.040,
        pulse: 0.95,
        phase: 0.5,
        driftSpeed: 0.55,
        driftRange: 32
      },
      {
        baseX: -90,
        baseYRel: 0.72,
        rBase: 0.30,
        rVar: 0.09,
        color: '255, 100, 20',
        baseA: 0.038,
        pulse: 0.85,
        phase: 3.9,
        driftSpeed: 0.40,
        driftRange: 42
      },
      {
        baseX: -190,
        baseYRel: 0.92,
        rBase: 0.36,
        rVar: 0.12,
        color: '255, 70, 10',
        baseA: 0.045,
        pulse: 0.55,
        phase: 1.8,
        driftSpeed: 0.20,
        driftRange: 55
      },
      {
        baseX: -15,
        baseYRel: 0.78,
        rBase: 0.26,
        rVar: 0.07,
        color: '255, 150, 40',
        baseA: 0.032,
        pulse: 1.15,
        phase: 4.5,
        driftSpeed: 0.50,
        driftRange: 28
      },
      {
        baseX: -140,
        baseYRel: 0.98,
        rBase: 0.32,
        rVar: 0.10,
        color: '255, 80, 15',
        baseA: 0.042,
        pulse: 0.70,
        phase: 2.3,
        driftSpeed: 0.30,
        driftRange: 48
      },
      {
        baseX: -50,
        baseYRel: 0.65,
        rBase: 0.24,
        rVar: 0.07,
        color: '255, 110, 25',
        baseA: 0.030,
        pulse: 1.05,
        phase: 5.1,
        driftSpeed: 0.60,
        driftRange: 30
      },
      {
        baseX: -200,
        baseYRel: 0.85,
        rBase: 0.38,
        rVar: 0.11,
        color: '255, 60, 8',
        baseA: 0.048,
        pulse: 0.45,
        phase: 0.9,
        driftSpeed: 0.22,
        driftRange: 50
      },
    ];

    const blobStates = blobDefs.map(b => ({
      driftX: randomRange(-b.driftRange * 0.5, b.driftRange * 0.5),
      driftY: randomRange(-b.driftRange * 0.3, b.driftRange * 0.3),
      driftTargetX: randomRange(-b.driftRange, b.driftRange),
      driftTargetY: randomRange(-b.driftRange * 0.6, b.driftRange * 0.6),
      driftTimer: Math.floor(randomRange(20, 70)),
    }));

    let flare = null;

    function startFlare() {
      const isBig = Math.random() < 0.12;
      flare = {
        intensity: 0,
        targetIntensity: isBig ? randomRange(0.10, 0.20) : randomRange(0.05, 0.10),
        radiusMul: isBig ? randomRange(1.4, 2.2) : randomRange(1.0, 1.3),
        phase: 'rise',
        timer: 0,
        holdDuration: Math.floor(randomRange(60, 160)),
        riseSpeed: randomRange(0.002, 0.006),
        fallSpeed: randomRange(0.0015, 0.004),
      };
    }

    function updateGlow() {
      const w = canvas.width;
      const h = canvas.height;
      const time = Date.now() * 0.001;

      if (!flare && Math.random() < 0.006) {
        startFlare();
      }

      if (flare) {
        if (flare.phase === 'rise') {
          flare.intensity += flare.riseSpeed;
          if (flare.intensity >= flare.targetIntensity) {
            flare.intensity = flare.targetIntensity;
            flare.phase = 'hold';
            flare.timer = 0;
          }
        } else if (flare.phase === 'hold') {
          flare.timer += 1;
          flare.intensity = flare.targetIntensity + Math.sin(flare.timer * 0.12) * 0.003;
          if (flare.timer >= flare.holdDuration) {
            flare.phase = 'fall';
          }
        } else if (flare.phase === 'fall') {
          flare.intensity -= flare.fallSpeed;
          if (flare.intensity <= 0) {
            flare.intensity = 0;
            flare = null;
          }
        }
      }

      ctx.globalCompositeOperation = 'screen';

      // Update chaotic drift for each blob
      blobDefs.forEach((b, i) => {
        const s = blobStates[i];
        s.driftTimer -= 1;
        if (s.driftTimer <= 0) {
          s.driftTargetX = randomRange(-b.driftRange, b.driftRange);
          s.driftTargetY = randomRange(-b.driftRange * 0.6, b.driftRange * 0.6);
          s.driftTimer = Math.floor(randomRange(30, 90));
        }
        s.driftX += (s.driftTargetX - s.driftX) * b.driftSpeed * 0.08;
        s.driftY += (s.driftTargetY - s.driftY) * b.driftSpeed * 0.08;

        const slowPulse = Math.sin(time * b.pulse + b.phase) * 0.5 + 0.5;
        const fastFlicker = Math.sin(time * 7.3 + i * 2.7) * 0.10
          + Math.cos(time * 10.1 + i * 1.4) * 0.07;

        const alpha = b.baseA * (0.60 + slowPulse * 0.40 + fastFlicker);
        const radius = Math.min(w, h) * (b.rBase + slowPulse * b.rVar);

        const cx = b.baseX + s.driftX + Math.sin(time * 0.4 + i) * 12;
        const cy = h * b.baseYRel + s.driftY + Math.cos(time * 0.3 + i * 1.6) * 10;

        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        grad.addColorStop(0, `rgba(${b.color}, ${Math.max(0, alpha)})`);
        grad.addColorStop(0.4, `rgba(${b.color}, ${Math.max(0, alpha * 0.42)})`);
        grad.addColorStop(0.75, `rgba(${b.color}, ${Math.max(0, alpha * 0.12)})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      });

      // Flare — a broad soft pulse that lifts several blobs at once
      if (flare && flare.intensity > 0.001) {
        const flareRadius = Math.max(w, h) * (0.5 + flare.radiusMul * 0.45);
        const cx = -80 + Math.sin(time * 0.25) * 40;
        const cy = h * 0.92 + Math.cos(time * 0.18) * 25;
        const a = flare.intensity;

        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, flareRadius);
        grad.addColorStop(0, `rgba(255, 110, 35, ${a})`);
        grad.addColorStop(0.25, `rgba(255, 85, 20, ${a * 0.55})`);
        grad.addColorStop(0.55, `rgba(255, 60, 10, ${a * 0.22})`);
        grad.addColorStop(0.85, `rgba(255, 40, 5, ${a * 0.06})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      }

      ctx.globalCompositeOperation = 'source-over';
    }

    function spawnParticle() {
      const h = canvas.height;
      const originX = randomRange(-50, -15);
      const originY = randomRange(0, h);

      const color = randomColor();
      const size = randomRange(0.5, 3) * sparkScaleRef.current;
      const life = randomRange(700, 1800);

      const vx = randomRange(0.5, 1.0);
      const vy = randomRange(-0.15, -0.4);

      particlesRef.current.push({
        x: originX,
        y: originY,
        vx,
        vy,
        size,
        life,
        maxLife: life,
        color,
        turbulenceOffset: randomRange(0, Math.PI * 2),
        flickerPhase: randomRange(0, Math.PI * 10),
        flickerSpeed: randomRange(0.01, 0.03),
        flickerBase: randomRange(0.35, 0.85),
        shapeType: Math.floor(randomRange(0, 5)),
        shapeRotation: randomRange(0, Math.PI * 2),
        shapeRotSpeed: randomRange(-0.03, 0.03),
      });
    }

    function updateAndDraw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      updateGlow();

      spawnTimerRef.current += 1;
      const spawnRate = 30;
      if (spawnTimerRef.current >= spawnRate) {
        spawnTimerRef.current = 0;
        const toSpawn = Math.floor(randomRange(1, 3));
        for (let i = 0; i < toSpawn; i++) {
          if (particlesRef.current.length < maxParticles) {
            spawnParticle();
          }
        }
      }

      const particles = particlesRef.current;
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.life -= 1;

        if (p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }

        p.turbulenceOffset += 0.05;
        const wobble = Math.sin(p.turbulenceOffset) * 0.3;

        p.x += p.vx + wobble;
        p.y += p.vy;
        p.vy += 0.001;

        const lifeRatio = p.life / p.maxLife;
        const fadeAlpha = lifeRatio * 0.9 + 0.1;

        p.flickerPhase += p.flickerSpeed;
        const flicker = Math.sin(p.flickerPhase) * 0.30
          + Math.sin(p.flickerPhase * 1.6 + 1.3) * 0.18
          + (Math.random() - 0.5) * 0.12;
        const flickerFactor = Math.max(0.1, Math.min(1, p.flickerBase + flicker));
        const alpha = fadeAlpha * flickerFactor;

        const currentSize = p.size * (0.5 + lifeRatio * 0.5);

        p.shapeRotation += p.shapeRotSpeed;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.shapeRotation);
        ctx.fillStyle = `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${alpha})`;
        ctx.beginPath();
        const s = currentSize;
        if (p.shapeType === 0) {
          ctx.moveTo(0, -s);
          ctx.lineTo(s * 0.55, 0);
          ctx.lineTo(0, s);
          ctx.lineTo(-s * 0.55, 0);
        } else if (p.shapeType === 1) {
          ctx.moveTo(0, -s);
          ctx.lineTo(s * 0.65, s * 0.55);
          ctx.lineTo(-s * 0.65, s * 0.55);
        } else if (p.shapeType === 2) {
          ctx.moveTo(0, -s);
          ctx.lineTo(s * 0.75, -s * 0.15);
          ctx.lineTo(s * 0.25, s * 0.85);
          ctx.lineTo(-s * 0.55, s * 0.35);
        } else if (p.shapeType === 3) {
          const w = s * 0.35;
          ctx.moveTo(0, -s);
          ctx.lineTo(w, -w);
          ctx.lineTo(s, 0);
          ctx.lineTo(w, w);
          ctx.lineTo(0, s);
          ctx.lineTo(-w, w);
          ctx.lineTo(-s, 0);
          ctx.lineTo(-w, -w);
        } else {
          ctx.moveTo(0, -s);
          ctx.lineTo(s * 0.4, -s * 0.3);
          ctx.lineTo(s * 0.8, s * 0.2);
          ctx.lineTo(s * 0.2, s * 0.9);
          ctx.lineTo(-s * 0.5, s * 0.5);
          ctx.lineTo(-s * 0.7, 0);
        }
        ctx.closePath();
        ctx.fill();

        if (currentSize > 1.2) {
          ctx.beginPath();
          ctx.arc(0, 0, s * 2.2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${alpha * 0.12})`;
          ctx.fill();
        }
        ctx.restore();
      }

      animRef.current = requestAnimationFrame(updateAndDraw);
    }

    animRef.current = requestAnimationFrame(updateAndDraw);

    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
      particlesRef.current = [];
    };
  }, []);

  return <canvas ref={canvasRef} className="campfire-sparks-canvas" aria-hidden="true"/>;
};

export default CampfireSparks;
